import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSettings } from '../redux/slices/settingsSlice';

/**
 * Unternehmensform (Taxi- oder Mietwagenunternehmen) und die daraus
 * abgeleitete Wortwahl der Oberfläche.
 *
 * Quelle der Wahrheit ist `appSettings/general.unternehmensform`
 * ('taxi' | 'mietwagen', Standard 'taxi'). Eingestellt wird sie im Portal
 * unter Einstellungen → „Betrieb & Disposition"; dieselbe Angabe steuert
 * zusätzlich die Verkehrsform der Quittungen (Umsatzsteuer) und wird von der
 * Kunden-/Fahrer-App gelesen.
 *
 * Der Wert ändert nur die **Anzeige** — Datenmodell, Feldnamen und
 * Firestore-Werte bleiben unverändert.
 *
 * Nicht umbenannt werden bewusst:
 * - Eigennamen („Taxi Butler", Firmenname aus dem Branding),
 * - steuer-/sozialrechtliche Fachbegriffe („Taxitarif" nach § 133 SGB V,
 *   „Taxi 7 % USt", Muster-4-Feld „Taxi/Mietwagen", Aufsichtsbehörde),
 * - die NextCab-Landingpage (richtet sich an beide Unternehmensformen).
 *
 * Siehe nextcab_mobile/docs/general/unternehmensform_konzept.md
 */

export const UNTERNEHMENSFORM_TAXI = 'taxi';
export const UNTERNEHMENSFORM_MIETWAGEN = 'mietwagen';

/** Fremde/fehlende Werte fallen immer auf 'taxi' zurück (bisheriges Verhalten). */
export const normalizeUnternehmensform = (value) => (
  value === UNTERNEHMENSFORM_MIETWAGEN ? UNTERNEHMENSFORM_MIETWAGEN : UNTERNEHMENSFORM_TAXI
);

export const UNTERNEHMENSFORM_OPTIONS = [
  {
    value: UNTERNEHMENSFORM_TAXI,
    label: 'Taxiunternehmen',
    hint: 'Taxi-Konzession nach § 47 PBefG — Tarifpflicht, 7 % USt bis 50 km'
  },
  {
    value: UNTERNEHMENSFORM_MIETWAGEN,
    label: 'Mietwagenunternehmen',
    hint: 'Mietwagen nach § 49 PBefG — Rückkehrpflicht, immer 19 % USt'
  }
];

/**
 * Baut das Wortschatz-Objekt zu einer Unternehmensform.
 *
 * `pick(taxiText, mietwagenText)` ist für ganze Sätze gedacht: „Taxi" ist
 * sächlich, „Wagen" männlich — ein reiner Wortaustausch würde Artikel und
 * Adjektivendungen zerstören („ein freies Taxi" → „ein freier Wagen").
 */
export const buildTransportWording = (unternehmensform) => {
  const form = normalizeUnternehmensform(unternehmensform);
  const isMietwagen = form === UNTERNEHMENSFORM_MIETWAGEN;

  return {
    unternehmensform: form,
    isMietwagen,
    /** Fahrzeug, Singular — ohne Artikel verwenden („Taxi bestellen"). */
    vehicle: isMietwagen ? 'Wagen' : 'Taxi',
    /** Fahrzeug, Plural. */
    vehiclePlural: isMietwagen ? 'Wagen' : 'Taxis',
    /** Fahrer in Kundenrichtung. */
    driver: isMietwagen ? 'Fahrer' : 'Taxifahrer',
    /** Eine Fahrt („Ihre Taxifahrt ist bezahlt"). */
    ride: isMietwagen ? 'Fahrt' : 'Taxifahrt',
    ridePlural: isMietwagen ? 'Fahrten' : 'Taxifahrten',
    /** Das Unternehmen selbst. */
    company: isMietwagen ? 'Mietwagenunternehmen' : 'Taxiunternehmen',
    /** Fahrzeugtyp 1 in Auswahllisten. */
    standardVehicle: isMietwagen ? 'Standard-Wagen' : 'Standard-Taxi',
    /** Ganze Sätze, bei denen die Grammatik mitwandert. */
    pick: (taxiText, mietwagenText) => (isMietwagen ? mietwagenText : taxiText)
  };
};

/** Wortwahl ohne Unternehmensform-Kenntnis (Fallback = Taxi). */
export const defaultTransportWording = buildTransportWording(UNTERNEHMENSFORM_TAXI);

/**
 * Hook für Komponenten. Lädt `appSettings/general` bei Bedarf selbst nach —
 * die Einstellungen werden im Portal nicht zentral beim Start geholt, und
 * öffentliche Seiten (z. B. die Event-Infoseite) laufen ganz ohne Login.
 * Der Read ist öffentlich erlaubt (firestore.rules: appSettings read = true).
 */
export const useTransportWording = () => {
  const dispatch = useDispatch();
  const unternehmensform = useSelector((state) => state.settings?.global?.unternehmensform);
  const isInitialized = useSelector((state) => state.settings?.isInitialized);
  const isLoading = useSelector((state) => state.settings?.isLoading);

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      dispatch(fetchSettings());
    }
  }, [dispatch, isInitialized, isLoading]);

  return useMemo(() => buildTransportWording(unternehmensform), [unternehmensform]);
};
